/**
 * Lettre libre au format A4.
 *
 * L'application sait produire devis, factures et documents RH, mais il reste
 * toujours un courrier qui n'entre dans aucune case : une relance à une régie,
 * une demande à une assurance. Celui-ci reprend l'en-tête de l'entreprise et
 * la présentation suisse — expéditeur en haut à gauche, destinataire à droite,
 * lieu et date au-dessus de l'objet.
 */

import { LANG, localeDates, tr, trf } from './i18n';
import { tamponVoulu } from './tampon';

/**
 * Neutralise ce qui serait interprété comme du balisage.
 *
 * Le corps de la lettre et les coordonnées sont saisis par l'utilisateur puis
 * insérés dans une page HTML. Sans cette précaution, une adresse contenant un
 * chevron casserait la mise en page, et un contenu malveillant s'exécuterait
 * dans la fenêtre d'impression.
 *
 * @param {*} x valeur à insérer
 * @returns {string} la valeur, sans balisage actif
 */
export function echappe(x) {
  return String(x ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Ville de l'entreprise, le NPA retiré. */
export function villeDe(npa) {
  return String(npa || '')
    .replace(/^\d+\s*/, '')
    .trim();
}

/**
 * Les règles de la feuille : en-tête, adresse, corps, signature.
 *
 * <p>En-tête : le logo d'un côté, les coordonnées de l'autre, alignés sur leur
 * milieu, puis un filet. Le logo était petit et collé au texte : il flottait
 * au-dessus de l'adresse sans s'y rattacher. L'en-tête garde au moins 30 mm
 * (27 mm, marge comprise, et 3 mm d'écart) : l'adresse du destinataire
 * commence à 50 mm du haut de la feuille, dans la fenêtre de l'enveloppe,
 * avec ou sans logo.
 */
const REGLES_FEUILLE = [
  '.feuille{width:210mm;min-height:297mm;margin:12px auto;padding:20mm 20mm 20mm 25mm;background:#fff;box-shadow:0 2px 12px rgba(0,0,0,.25)}',
  '.entete{display:flex;align-items:center;justify-content:space-between;gap:10mm;min-height:27mm;padding-bottom:3mm;margin-bottom:3mm;border-bottom:0.6pt solid #b8c0cc}',
  '.entete .exp{text-align:right}',
  '.entete.sans-logo{justify-content:flex-start}',
  '.entete.sans-logo .exp{text-align:left}',
  '.entete.droite{flex-direction:row-reverse}',
  '.entete.droite .exp{text-align:left}',
  '.entete.milieu{flex-direction:column;justify-content:center;text-align:center;gap:3mm}',
  '.entete.milieu .exp{text-align:center}',
  '.logo{display:block;max-width:75mm;max-height:24mm;object-fit:contain}',
  '.exp{font-size:9.5pt;line-height:1.45}',
  '.feuille{position:relative}',
  '.feuille>*{position:relative;z-index:1}',
  '.fond{position:absolute;z-index:0;pointer-events:none;max-height:none}',
  '.dest{margin-left:93mm;width:80mm;min-height:40mm;white-space:pre-line}',
  '.lieu{margin:6mm 0 10mm}',
  '.obj{font-weight:bold;margin-bottom:8mm}',
  '.corps{white-space:pre-wrap}',
  '.signature{margin-top:12mm;break-inside:avoid;page-break-inside:avoid}',
  '.trait{width:60mm;border-bottom:1px solid #000;height:18mm}',
  '.signature img{display:block;max-height:20mm;max-width:60mm;margin:2mm 0 1mm}',
  '.signature .fonction{font-size:9.5pt;color:#555}',
  '.signature .images{display:flex;align-items:center;gap:5mm;margin:2mm 0 1mm}',
  '.signature .images img{margin:0}',
  '.signature img.tampon{max-height:26mm;max-width:32mm}',
  // Les photos suivent la signature, deux par rangée, comme une annexe :
  // chacune reste entière sur sa page, avec sa légende.
  '.photos-titre{margin-top:10mm;font-weight:bold;break-after:avoid;page-break-after:avoid}',
  '.photos{margin-top:4mm;display:grid;grid-template-columns:1fr 1fr;gap:5mm}',
  '.photo{margin:0;break-inside:avoid;page-break-inside:avoid}',
  '.photo img{display:block;width:100%;max-height:85mm;object-fit:contain;border:0.4pt solid #ccd2db;background:#f6f7f9}',
  '.photo figcaption{font-size:9pt;line-height:1.35;margin-top:1.5mm;color:#333}',
];

/**
 * Une photo se pose dans la lettre telle quelle : une image encodée (JPEG,
 * PNG ou WebP), ou une photo déjà enregistrée avec le courrier — jamais une
 * autre adresse ni un autre contenu.
 */
const PHOTO_SURE =
  /^(data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/=]+|(https?:\/\/[^\s"'<>]+)?\/api\/pieces\/\d+\/contenu)$/;

/**
 * Les photos jointes à la lettre, sûres et dans leur ordre.
 *
 * @param {Array<{src: string, legende?: string}>|undefined} photos photos saisies
 * @returns {Array<{src: string, legende: string}>} celles qui peuvent paraître
 */
export function photosDeLaLettre(photos) {
  return (Array.isArray(photos) ? photos : [])
    .filter((p) => p && typeof p.src === 'string' && PHOTO_SURE.test(p.src))
    .map((p) => ({ src: p.src, legende: String(p.legende || '').trim() }));
}

/**
 * Les règles de la feuille, chacune préfixée (pour l'impression depuis
 * l'application, où elles ne doivent toucher que la lettre).
 *
 * @param {string} prefixe sélecteur à mettre devant chaque règle, ou rien
 * @returns {string} la feuille de style
 */
function reglesFeuille(prefixe) {
  return REGLES_FEUILLE.map((r) =>
    prefixe
      ? r.replace(/^[^{]+/, (sel) =>
          sel
            .split(',')
            .map((x) => prefixe + ' ' + x.trim())
            .join(','),
        )
      : r,
  ).join('');
}

/**
 * Le contenu de la feuille seul (sans page autour), tel que l'écrit
 * {@link construitLettre}.
 */
function contenuFeuille(contenu, soc, maintenant) {
  const complet = construitLettre({ ...contenu, sansOutils: true }, soc, maintenant);
  return complet.slice(complet.indexOf('<div class="feuille">'), complet.lastIndexOf('<script>'));
}

/**
 * La lettre montrée dans l'application, sans cadre : la feuille A4 à sa vraie
 * taille, que l'écran réduit ensuite à sa largeur. Un cadre (iframe) ne se
 * réduisait pas sur l'iPhone : Safari l'agrandit à la taille de la feuille, qui
 * débordait alors de l'écran.
 *
 * @param {object} contenu comme pour {@link construitLettre}
 * @param {object} soc entreprise émettrice
 * @param {Date} [maintenant] date de la lettre
 * @returns {{css: string, html: string}} la feuille et ses règles, limitées à #apercu-lettre
 */
export function lettreAEcran(contenu, soc, maintenant = new Date()) {
  const P = '#apercu-lettre';
  return {
    html: contenuFeuille(contenu, soc, maintenant),
    css:
      P +
      '{width:210mm;font-family:Arial,Helvetica,sans-serif;font-size:11pt;line-height:1.5;color:#000;text-align:left}' +
      P +
      ' *{box-sizing:border-box}' +
      reglesFeuille(P) +
      P +
      ' .feuille{margin:0}',
  };
}

/**
 * La lettre à imprimer depuis l'application même, sans fenêtre ni cadre :
 * le contenu de la feuille et une feuille de style qui, à l'impression, ne
 * montre qu'elle. Imprimer un cadre imprimait, sur certains téléphones, la
 * page entière de l'application.
 *
 * @param {object} contenu comme pour {@link construitLettre}
 * @param {object} soc entreprise émettrice
 * @param {Date} [maintenant] date de la lettre
 * @returns {{css: string, html: string}} la feuille et ses règles
 */
export function lettreAImprimer(contenu, soc, maintenant = new Date()) {
  const P = '#impression-lettre';
  return {
    html: contenuFeuille(contenu, soc, maintenant),
    css:
      '@media screen{' +
      P +
      '{display:none}}' +
      '@media print{' +
      '@page{size:A4;margin:20mm 20mm 20mm 25mm}' +
      'html,body{background:#fff!important}' +
      'body>*:not(' +
      P +
      '){display:none!important}' +
      P +
      '{display:block;font-family:Arial,Helvetica,sans-serif;font-size:11pt;line-height:1.5;color:#000}' +
      P +
      ' *{box-sizing:border-box}' +
      reglesFeuille(P) +
      P +
      ' .fond{position:fixed}' +
      P +
      ' .feuille{width:auto;min-height:0;margin:0;padding:0;box-shadow:none}}',
  };
}

/**
 * Compose la lettre en HTML : une vraie feuille A4, qui se lit à l'écran comme
 * elle sortira de l'imprimante.
 *
 * <p>Mise en page suisse, pour une enveloppe à fenêtre à droite : marges de
 * 20 mm (25 mm à gauche), adresse du destinataire à 50 mm du haut et 118 mm du
 * bord gauche. À l'écran, la feuille est posée sur un fond gris, avec un bouton
 * pour l'imprimer ou l'enregistrer en PDF ; à l'impression, il ne reste que la
 * lettre, et une lettre longue continue sur les pages suivantes.
 *
 * @param {object} lettre contenu saisi
 * @param {boolean} [lettre.avecLogo] porter le logo de l'entreprise, s'il y en a un
 * @param {object} soc coordonnées de l'entreprise
 * @param {Date} [maintenant] date portée par la lettre
 * @returns {string} le document complet
 */
export function construitLettre(
  {
    dest,
    destAdr,
    objet,
    corps,
    avecLogo,
    logo: placeLogo,
    sansOutils,
    photos,
    signature,
    signataireNom,
    signataireFonction,
  },
  soc,
  maintenant = new Date(),
) {
  // Où va le logo : en tête de la lettre, en fond de page (filigrane, comme
  // sur les offres et les factures), ou nulle part. L'ancienne case à cocher
  // (avecLogo) vaut « en tête » ou « sans ».
  const place = ['entete', 'fond', 'les-deux', 'aucun'].includes(placeLogo)
    ? placeLogo
    : avecLogo === false
      ? 'aucun'
      : 'entete';
  const date = maintenant.toLocaleDateString(localeDates(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const ville = villeDe(soc?.npa);
  const lignesExpediteur = [
    '<b>' + echappe(soc?.nom) + '</b>',
    echappe(soc?.adr),
    echappe(soc?.npa),
    soc?.tel ? echappe(trf('Tél : {0}', soc.tel)) : '',
    soc?.email ? echappe(soc.email) : '',
  ].filter(Boolean);
  // « Les deux » : le logo en tête et, en filigrane, au fond de la page —
  // comme les offres et les factures qui portent les deux.
  const logo = (place === 'entete' || place === 'les-deux') && soc?.logo ? soc.logo : '';
  const fond = (place === 'fond' || place === 'les-deux') && soc?.logo ? soc.logo : '';
  // Le filigrane suit le réglage des documents (Réglages → Documents).
  const f = soc?.filigrane || {};
  const nombre = (v, d) => (Number.isFinite(+v) && v !== null && v !== '' ? +v : d);
  const fondCss = {
    top: nombre(f.y, 50) + '%',
    left: nombre(f.x, 50) + '%',
    width: nombre(f.taille, 62) + '%',
    opacity: nombre(f.opacite, 12) / 100,
    transform: 'translate(-50%,-50%) rotate(' + nombre(f.rotation, 0) + 'deg)',
  };
  const fondStyle = Object.entries(fondCss)
    .map(([k, v]) => k + ':' + v)
    .join(';');
  // Toujours les coordonnées de l'entreprise à gauche et le logo à droite,
  // comme le PDF du serveur (documents/_commun.html) : un ancien réglage
  // « Gauche » faisait encore voir à l'écran la lettre en miroir de l'envoi.
  const position = 'droite';
  const lieuDate = ville ? trf('{0}, le {1}', echappe(ville), date) : trf('Le {0}', date);
  const titre = String(objet || '').trim() || tr('Courrier');

  return (
    '<!doctype html><html lang="' +
    echappe(LANG) +
    '"><head><meta charset="utf-8">' +
    // La feuille entière tient dans l'écran du téléphone, sans zoom.
    '<meta name="viewport" content="width=840">' +
    '<title>' +
    echappe(titre) +
    '</title>' +
    '<style>' +
    '@page{size:A4;margin:20mm 20mm 20mm 25mm}' +
    '*{box-sizing:border-box}' +
    'html{background:#d9dde3}' +
    'body{margin:0;font-family:Arial,Helvetica,sans-serif;font-size:11pt;line-height:1.5;color:#000}' +
    '.outils{position:sticky;top:0;z-index:1;display:flex;justify-content:center;padding:10px;background:#1c2b45}' +
    '.outils button{font:700 15px Arial,Helvetica,sans-serif;color:#1c2b45;background:#fff;border:0;border-radius:8px;padding:10px 18px;cursor:pointer}' +
    reglesFeuille('') +
    // À l'impression, le fond se pose sur chaque page, au même endroit.
    // À l'écran, la feuille se réduit à la largeur du téléphone ; imprimée,
    // elle garde sa taille réelle.
    '@media screen{body{zoom:var(--k,1)}}' +
    '@media print{html{background:#fff}.outils{display:none}.fond{position:fixed}' +
    '.feuille{width:auto;min-height:0;margin:0;padding:0;box-shadow:none}}' +
    '</style></head><body>' +
    // Montrée dans l'application, la feuille reçoit ses boutons de l'écran
    // qui l'entoure (retour, impression) : elle n'en porte pas.
    (sansOutils
      ? ''
      : '<div class="outils"><button type="button" onclick="window.print()">' +
        echappe(tr('🖨️ Imprimer ou enregistrer en PDF')) +
        '</button></div>') +
    '<div class="feuille">' +
    (fond
      ? '<img class="fond" src="' + echappe(fond) + '" alt="" style="' + fondStyle + '">'
      : '') +
    '<div class="entete ' +
    (logo ? position : 'sans-logo') +
    '">' +
    (logo ? '<img class="logo" src="' + echappe(logo) + '" alt="">' : '') +
    '<div class="exp">' +
    lignesExpediteur.join('<br>') +
    '</div></div>' +
    '<div class="dest"><b>' +
    echappe(dest) +
    '</b>\n' +
    echappe(destAdr) +
    '</div>' +
    '<div class="lieu">' +
    lieuDate +
    '</div>' +
    (String(objet || '').trim()
      ? '<div class="obj">' + echappe(trf('Objet : {0}', objet)) + '</div>'
      : '') +
    '<div class="corps">' +
    echappe(corps) +
    '</div>' +
    blocSignature(soc, signature, signataireNom, signataireFonction) +
    blocPhotos(photosDeLaLettre(photos)) +
    '</div>' +
    '<script>(function(){function k(){var l=document.documentElement.clientWidth;' +
    'document.documentElement.style.setProperty("--k",Math.min(1,l/840))}' +
    'k();window.addEventListener("resize",k)})()</script>' +
    '</body></html>'
  );
}

/** Les photos après la signature, ou rien sans photo. */
function blocPhotos(photos) {
  if (!photos.length) return '';
  return (
    '<div class="photos-titre">' +
    echappe(tr('Photos')) +
    '</div><div class="photos">' +
    photos
      .map(
        (p) =>
          '<figure class="photo"><img src="' +
          echappe(p.src) +
          '" alt="">' +
          (p.legende ? '<figcaption>' + echappe(p.legende) + '</figcaption>' : '') +
          '</figure>',
      )
      .join('') +
    '</div>'
  );
}

/**
 * La signature : l'entreprise, puis la signature posée (dessinée ou
 * enregistrée) et le nom de qui signe ; sans signature, un trait à signer.
 */
function blocSignature(soc, signature, nom, fonction) {
  const image = /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/.test(String(signature || ''))
    ? signature
    : '';
  // Le tampon de l'entreprise, à côté de la signature, si Réglages le veut.
  const tampon =
    tamponVoulu(soc, 'courrier') && /^data:image\//.test(String(soc?.tampon || ''))
      ? '<img class="tampon" src="' + echappe(soc.tampon) + '" alt="">'
      : '';
  return (
    '<div class="signature">' +
    echappe(soc?.nom) +
    (tampon
      ? '<div class="images">' +
        tampon +
        (image ? '<img src="' + image + '" alt="">' : '') +
        '</div>'
      : image
        ? '<img src="' + image + '" alt="">'
        : '<div class="trait"></div>') +
    (nom ? '<div>' + echappe(nom) + '</div>' : '') +
    (fonction ? '<div class="fonction">' + echappe(fonction) + '</div>' : '') +
    '</div>'
  );
}
